import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { 
  Car, 
  Truck, 
  Search, 
  Edit,
  Phone,
  Mail,
  User,
  ChevronRight,
  RefreshCw,
  X,
  Calendar,
  Download,
  Upload,
  FileUp,
  FileDown,
  Gauge,
  Fuel
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const VEHICLE_TYPE_LABELS = {
  car: 'Personbil',
  van: 'Varebil',
  truck: 'Lastebil',
  motorcycle: 'Motorsykkel',
  trailer: 'Tilhenger',
  other: 'Annet'
};

const VEHICLE_TYPE_ICONS = {
  car: Car,
  van: Truck,
  truck: Truck,
  motorcycle: Car,
  trailer: Truck,
  other: Car
};

const AdminVehicles = () => {
  const { token } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_type: {} });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [vehicleCustomer, setVehicleCustomer] = useState(null);
  const [vehicleBookings, setVehicleBookings] = useState([]);
  const [editForm, setEditForm] = useState({});
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importMode, setImportMode] = useState('update');
  const [importing, setImporting] = useState(false);

  const fetchVehicles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/vehicles?limit=200`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      let vehicleData = response.data;
      
      // Client-side filter by type
      if (typeFilter !== 'all') {
        vehicleData = vehicleData.filter(v => v.vehicle_type === typeFilter);
      }
      
      setVehicles(vehicleData);
    } catch (error) {
      console.error('Feil ved henting av kjøretøy:', error);
    }
  }, [token, typeFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/vehicles/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Feil ved henting av statistikk:', error);
    }
  }, [token]);

  const searchVehicles = useCallback(async (query) => {
    if (!query || query.length < 2) {
      fetchVehicles();
      return;
    }
    try {
      const response = await axios.get(`${API}/vehicles/search?q=${encodeURIComponent(query)}&limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicles(response.data);
    } catch (error) {
      console.error('Feil ved søk:', error);
    }
  }, [token, fetchVehicles]);

  const fetchVehicleDetails = async (vehicle) => {
    try {
      // Get customer info
      if (vehicle.customer_id) {
        const customerRes = await axios.get(`${API}/customers/${vehicle.customer_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setVehicleCustomer(customerRes.data);
      } else {
        setVehicleCustomer(null);
      }
      
      // Get bookings for this vehicle
      const bookingsRes = await axios.get(`${API}/bookings?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const vehicleBookings = bookingsRes.data.filter(b => 
        b.vehicle_id === vehicle.id || 
        b.reg_number?.toUpperCase().replace(/\s/g, '') === vehicle.reg_number?.toUpperCase().replace(/\s/g, '')
      );
      setVehicleBookings(vehicleBookings);
    } catch (error) {
      console.error('Feil ved henting av kjøretøydetaljer:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchVehicles(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchVehicles, fetchStats]);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchVehicles(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchVehicles]);

  const openVehicleDetail = async (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowDetailModal(true);
    await fetchVehicleDetails(vehicle);
  };

  const openEditVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setEditForm({
      reg_number: vehicle.reg_number || '',
      make: vehicle.make || '',
      model: vehicle.model || '',
      year: vehicle.year || '',
      vehicle_type: vehicle.vehicle_type || 'car',
      fuel_type: vehicle.fuel_type || '',
      notes: vehicle.notes || ''
    });
    setShowEditModal(true);
  };

  const saveVehicle = async () => {
    try {
      await axios.put(`${API}/vehicles/${selectedVehicle.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowEditModal(false);
      fetchVehicles();
      fetchStats();
    } catch (error) {
      console.error('Feil ved lagring:', error);
      alert('Kunne ikke lagre kjøretøy: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Export vehicles to CSV
  const exportVehicles = async () => {
    try {
      const params = typeFilter !== 'all' ? `?vehicle_type=${typeFilter}` : '';
      const response = await axios.get(`${API}/vehicles/export/csv${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `kjoretoy_${new Date().toISOString().split('T')[0]}.tsv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Feil ved eksport:', error);
      alert('Kunne ikke eksportere kjøretøy');
    }
  };

  // Handle CSV file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImportFile(file);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      const lines = content.split('\n');
      const headers = lines[0].split(/[\t,]/);
      
      const preview = [];
      for (let i = 1; i < Math.min(6, lines.length); i++) {
        if (lines[i].trim()) {
          const values = lines[i].split(/[\t,]/);
          const row = {};
          headers.forEach((header, idx) => {
            row[header.trim()] = values[idx]?.trim() || '';
          });
          preview.push(row);
        }
      }
      setImportPreview(preview);
    };
    reader.readAsText(file);
  };

  // Handle vehicle import
  const handleImport = async () => {
    if (!importFile) return;
    
    setImporting(true);
    
    try {
      const content = await importFile.text();
      
      const response = await axios.post(
        `${API}/vehicles/import/csv?mode=${importMode}`,
        content,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'text/plain'
          }
        }
      );
      
      const result = response.data;
      
      let message = `Import fullført!\n\n`;
      message += `Totalt rader: ${result.total}\n`;
      if (result.created > 0) message += `Nye kjøretøy: ${result.created}\n`;
      if (result.updated > 0) message += `Oppdaterte: ${result.updated}\n`;
      if (result.skipped > 0) message += `Hoppet over: ${result.skipped}\n`;
      if (result.failed > 0) {
        message += `Feilet: ${result.failed}\n`;
        if (result.errors.length > 0) {
          message += `\nFeil:\n${result.errors.slice(0, 5).join('\n')}`;
          if (result.errors.length > 5) message += `\n... og ${result.errors.length - 5} flere feil`;
        }
      }
      
      alert(message);
      
      setShowImportModal(false);
      setImportFile(null);
      setImportPreview([]);
      fetchVehicles();
      fetchStats();
      
    } catch (error) {
      console.error('Import feilet:', error);
      alert('Kunne ikke importere: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  // Download CSV template
  const downloadTemplate = () => {
    const template = `reg_number\tmake\tmodel\tyear\tvehicle_type\tfuel_type\tcustomer_email\tnotes
AB 12345\tToyota\tHilux\t2020\tcar\tdiesel\tola@example.com\tEU-kontroll 2025
CD 67890\tVolkswagen\tTransporter\t2019\tvan\tdiesel\tkontakt@bedriften.no\tFast serviceavtale`;
    
    const blob = new Blob([template], { type: 'text/tab-separated-values' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'kjoretoy_mal.tsv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('nb-NO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getVehicleIcon = (type) => {
    const Icon = VEHICLE_TYPE_ICONS[type] || Car;
    return <Icon className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Kjøretøy</h1>
          <p className="text-gray-400 mt-1">Administrer kjøretøyregister og se servicehistorikk</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={exportVehicles}
            variant="outline" 
            className="border-green-600 text-green-400 hover:bg-green-900/30"
            data-testid="export-vehicles-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Eksporter CSV
          </Button>
          <Button 
            onClick={() => setShowImportModal(true)}
            variant="outline" 
            className="border-blue-600 text-blue-400 hover:bg-blue-900/30"
            data-testid="import-vehicles-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Importer CSV
          </Button>
          <Button 
            onClick={() => {
              fetchVehicles();
              fetchStats();
            }}
            variant="outline" 
            className="border-gray-600 text-gray-300 hover:bg-gray-800"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Oppdater
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card 
          className={`bg-gray-800/50 border-gray-700 cursor-pointer transition-all ${typeFilter === 'all' ? 'ring-2 ring-red-500' : 'hover:bg-gray-800'}`}
          onClick={() => setTypeFilter('all')}
        >
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-gray-700 rounded-lg">
              <Car className="w-5 h-5 text-gray-300" />
            </div>
            <div>
              <p className="text-xl font-bold text-white">{stats.total}</p>
              <p className="text-xs text-gray-400">Totalt</p>
            </div>
          </CardContent>
        </Card>

        {Object.entries(stats.by_type || {}).map(([type, count]) => (
          <Card 
            key={type}
            className={`bg-gray-800/50 border-gray-700 cursor-pointer transition-all ${typeFilter === type ? 'ring-2 ring-blue-500' : 'hover:bg-gray-800'}`}
            onClick={() => setTypeFilter(type)}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-blue-900/50 rounded-lg">
                {getVehicleIcon(type)}
              </div>
              <div>
                <p className="text-xl font-bold text-white">{count}</p>
                <p className="text-xs text-gray-400">{VEHICLE_TYPE_LABELS[type] || type}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search and Filter */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Søk på kjennemerke, merke eller modell..."
                className="pl-10 bg-gray-900 border-gray-600 text-white placeholder:text-gray-500"
                data-testid="vehicle-search-input"
              />
            </div>
            {typeFilter !== 'all' && (
              <Button
                onClick={() => setTypeFilter('all')}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                <X className="w-4 h-4 mr-2" />
                Fjern filter
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Vehicle List */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Car className="w-5 h-5" />
            Kjøretøyliste ({vehicles.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {vehicles.length === 0 ? (
            <div className="text-center py-12">
              <Car className="w-16 h-16 mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400 text-lg">Ingen kjøretøy funnet</p>
              {searchQuery && (
                <p className="text-gray-500 mt-2">Prøv et annet søkeord</p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {vehicles.map((vehicle) => (
                <div
                  key={vehicle.id}
                  className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg hover:bg-gray-900 transition-all cursor-pointer group"
                  onClick={() => openVehicleDetail(vehicle)}
                  data-testid={`vehicle-row-${vehicle.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-gray-800 rounded-lg text-blue-400">
                      {getVehicleIcon(vehicle.vehicle_type)}
                    </div>
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-white text-lg">{vehicle.reg_number}</span>
                        <Badge variant="outline" className="text-gray-400 border-gray-600">
                          {VEHICLE_TYPE_LABELS[vehicle.vehicle_type] || vehicle.vehicle_type}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
                        {vehicle.make && (
                          <span>{vehicle.make} {vehicle.model} {vehicle.year && `(${vehicle.year})`}</span>
                        )}
                        {vehicle.fuel_type && (
                          <span className="flex items-center gap-1">
                            <Fuel className="w-3 h-3" />
                            {vehicle.fuel_type}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {vehicle.last_service_date && (
                      <div className="text-right text-sm">
                        <p className="text-gray-500">Siste service</p>
                        <p className="text-gray-400">{formatDate(vehicle.last_service_date)}</p>
                      </div>
                    )}
                    <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-gray-400 transition-colors" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Vehicle Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-white flex items-center gap-3">
              {selectedVehicle && getVehicleIcon(selectedVehicle.vehicle_type)}
              {selectedVehicle?.reg_number}
            </DialogTitle>
          </DialogHeader>

          {selectedVehicle && (
            <div className="space-y-6">
              {/* Vehicle Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="bg-gray-800/50 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Kjøretøyinfo</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Kjennemerke:</span>
                      <span className="text-white font-semibold">{selectedVehicle.reg_number}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Type:</span>
                      <Badge variant="outline" className="text-gray-300 border-gray-600">
                        {VEHICLE_TYPE_LABELS[selectedVehicle.vehicle_type] || selectedVehicle.vehicle_type}
                      </Badge>
                    </div>
                    {selectedVehicle.make && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Merke/Modell:</span>
                        <span className="text-white">{selectedVehicle.make} {selectedVehicle.model}</span>
                      </div>
                    )}
                    {selectedVehicle.year && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Årsmodell:</span>
                        <span className="text-white">{selectedVehicle.year}</span>
                      </div>
                    )}
                    {selectedVehicle.fuel_type && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Drivstoff:</span>
                        <span className="text-white">{selectedVehicle.fuel_type}</span>
                      </div>
                    )}
                    {selectedVehicle.last_km_reading && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Kilometerstand:</span>
                        <span className="text-white">{selectedVehicle.last_km_reading.toLocaleString()} km</span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Owner Info */}
                <Card className="bg-gray-800/50 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Eier</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {vehicleCustomer ? (
                      <>
                        <div className="flex items-center gap-3 text-gray-300">
                          <User className="w-4 h-4 text-gray-500" />
                          <span>{vehicleCustomer.name}</span>
                        </div>
                        <div className="flex items-center gap-3 text-gray-300">
                          <Phone className="w-4 h-4 text-gray-500" />
                          <a href={`tel:${vehicleCustomer.phone}`} className="hover:text-white">
                            {vehicleCustomer.phone}
                          </a>
                        </div>
                        <div className="flex items-center gap-3 text-gray-300">
                          <Mail className="w-4 h-4 text-gray-500" />
                          <a href={`mailto:${vehicleCustomer.email}`} className="hover:text-white">
                            {vehicleCustomer.email}
                          </a>
                        </div>
                        {vehicleCustomer.company_name && (
                          <div className="text-blue-400 mt-2">
                            {vehicleCustomer.company_name}
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-gray-500">Ingen eier registrert</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Service History */}
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Servicehistorikk ({vehicleBookings.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {vehicleBookings.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Ingen bookinger registrert</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {vehicleBookings.map((booking) => (
                        <div key={booking.id} className="p-3 bg-gray-900/50 rounded-lg flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="text-center">
                              <p className="text-white font-semibold">{formatDate(booking.date)}</p>
                              <p className="text-gray-500 text-sm">{booking.time_slot}</p>
                            </div>
                            <div>
                              <p className="text-gray-300">{booking.service_name || 'Service'}</p>
                              {booking.notes && (
                                <p className="text-gray-500 text-sm truncate max-w-xs">{booking.notes}</p>
                              )}
                            </div>
                          </div>
                          <Badge className={
                            booking.status === 'completed' ? 'bg-blue-600' :
                            booking.status === 'confirmed' ? 'bg-green-600' :
                            booking.status === 'cancelled' ? 'bg-red-600' :
                            'bg-yellow-600'
                          }>
                            {booking.status === 'completed' ? 'Fullført' :
                             booking.status === 'confirmed' ? 'Bekreftet' :
                             booking.status === 'cancelled' ? 'Kansellert' :
                             booking.status === 'pending' ? 'Venter' :
                             booking.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Notes */}
              {selectedVehicle.notes && (
                <Card className="bg-gray-800/50 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Notater</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-300 whitespace-pre-wrap">{selectedVehicle.notes}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              onClick={() => setShowDetailModal(false)}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              Lukk
            </Button>
            <Button
              onClick={() => {
                setShowDetailModal(false);
                openEditVehicle(selectedVehicle);
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Edit className="w-4 h-4 mr-2" />
              Rediger
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Vehicle Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Rediger kjøretøy</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Kjennemerke *</Label>
                <Input
                  value={editForm.reg_number}
                  onChange={(e) => setEditForm({...editForm, reg_number: e.target.value.toUpperCase()})}
                  className="bg-gray-800 border-gray-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Kjøretøytype</Label>
                <select
                  value={editForm.vehicle_type}
                  onChange={(e) => setEditForm({...editForm, vehicle_type: e.target.value})}
                  className="w-full p-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
                >
                  {Object.entries(VEHICLE_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Merke</Label>
                <Input
                  value={editForm.make}
                  onChange={(e) => setEditForm({...editForm, make: e.target.value})}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="Toyota"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Modell</Label>
                <Input
                  value={editForm.model}
                  onChange={(e) => setEditForm({...editForm, model: e.target.value})}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="Hilux"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Årsmodell</Label>
                <Input
                  type="number"
                  value={editForm.year}
                  onChange={(e) => setEditForm({...editForm, year: e.target.value})}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="2020"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Drivstoff</Label>
              <select
                value={editForm.fuel_type}
                onChange={(e) => setEditForm({...editForm, fuel_type: e.target.value})}
                className="w-full p-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
              >
                <option value="">Ikke angitt</option>
                <option value="petrol">Bensin</option>
                <option value="diesel">Diesel</option>
                <option value="electric">Elektrisk</option>
                <option value="hybrid">Hybrid</option>
                <option value="other">Annet</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Notater</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                className="bg-gray-800 border-gray-600 text-white min-h-[100px]"
                placeholder="Interne notater om kjøretøyet..."
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              onClick={() => setShowEditModal(false)}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              Avbryt
            </Button>
            <Button
              onClick={saveVehicle}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Lagre endringer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={(open) => {
        setShowImportModal(open);
        if (!open) {
          setImportFile(null);
          setImportPreview([]);
        }
      }}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-white flex items-center gap-3">
              <Upload className="w-6 h-6 text-blue-400" />
              Importer kjøretøy fra CSV
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Mode Selection */}
            <div className="space-y-4">
              <Label className="text-lg font-medium text-gray-200">Importmodus</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  onClick={() => setImportMode('update')}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    importMode === 'update' 
                      ? 'border-blue-500 bg-blue-900/30' 
                      : 'border-gray-600 hover:border-gray-500'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      checked={importMode === 'update'}
                      onChange={() => setImportMode('update')}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-semibold text-white">Oppdater</p>
                      <p className="text-sm text-gray-400 mt-1">
                        Legger til nye kjøretøy. Oppdaterer eksisterende hvis kjennemerke matcher.
                      </p>
                    </div>
                  </div>
                </div>
                <div
                  onClick={() => setImportMode('skip')}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    importMode === 'skip' 
                      ? 'border-green-500 bg-green-900/30' 
                      : 'border-gray-600 hover:border-gray-500'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      checked={importMode === 'skip'}
                      onChange={() => setImportMode('skip')}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-semibold text-white">Kun nye</p>
                      <p className="text-sm text-gray-400 mt-1">
                        Legger kun til nye kjøretøy. Hopper over hvis kjennemerke allerede finnes.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* File Upload */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-lg font-medium text-gray-200">Velg CSV-fil</Label>
                <Button
                  onClick={downloadTemplate}
                  variant="outline"
                  size="sm"
                  className="border-blue-500 text-blue-400 hover:bg-blue-900/30"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Last ned mal
                </Button>
              </div>
              
              <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                <input
                  type="file"
                  accept=".csv,.tsv,.txt"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="vehicle-csv-input"
                  data-testid="vehicle-csv-input"
                />
                <label htmlFor="vehicle-csv-input" className="cursor-pointer">
                  <FileUp className="w-12 h-12 mx-auto text-gray-500 mb-3" />
                  <p className="text-gray-300">
                    {importFile ? importFile.name : 'Klikk for å velge fil'}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Støtter CSV og TSV-filer
                  </p>
                </label>
              </div>
            </div>

            {/* Expected Format */}
            <div className="bg-gray-800/50 rounded-lg p-4">
              <p className="font-medium text-gray-300 mb-2">Forventet format (kolonner):</p>
              <code className="text-xs bg-gray-900 p-2 rounded block overflow-x-auto text-gray-400">
                reg_number, make, model, year, vehicle_type, fuel_type, customer_email, notes
              </code>
              <p className="text-xs text-gray-500 mt-2">
                * reg_number og customer_email er obligatoriske<br/>
                * customer_email må tilhøre en eksisterende kunde<br/>
                * vehicle_type: car, van, truck, motorcycle, trailer, other
              </p>
            </div>

            {/* Preview */}
            {importPreview.length > 0 && (
              <div className="space-y-3">
                <Label className="text-lg font-medium text-gray-200">Forhåndsvisning</Label>
                <div className="overflow-x-auto border border-gray-700 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-800">
                      <tr>
                        <th className="p-2 text-left text-gray-400">Kjennemerke</th>
                        <th className="p-2 text-left text-gray-400">Merke</th>
                        <th className="p-2 text-left text-gray-400">Modell</th>
                        <th className="p-2 text-left text-gray-400">År</th>
                        <th className="p-2 text-left text-gray-400">Kunde</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.map((row, idx) => (
                        <tr key={idx} className="border-t border-gray-700">
                          <td className="p-2 text-gray-300 font-mono">{row.reg_number}</td>
                          <td className="p-2 text-gray-300">{row.make || '-'}</td>
                          <td className="p-2 text-gray-300">{row.model || '-'}</td>
                          <td className="p-2 text-gray-300">{row.year || '-'}</td>
                          <td className="p-2 text-gray-300">{row.customer_email || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              onClick={() => {
                setShowImportModal(false);
                setImportFile(null);
                setImportPreview([]);
              }}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleImport}
              disabled={!importFile || importing}
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
              data-testid="confirm-vehicle-import-btn"
            >
              {importing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Importerer...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Importer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminVehicles;
